/**
 * resolveOptimizerRowScope
 *
 * Turns the table's selection into the rows the commission optimizer considers.
 * The truth table (SYD-2429): nothing ticked is the whole table — CLW's
 * whole-BOM markdown needs no extra click; a live all-rows escalation is also
 * the whole table; anything else is the ticks that are on screen, which may be
 * empty when a filter or split has hidden every ticked row. That empty set is
 * deliberate: a forgotten selection must never widen to the whole table.
 *
 * Consumed by `ArTableOptimizerControls`, which resolves it from snapshots at
 * render time and hands the value into `useArTableOptimizer`. Pure — no React,
 * no Valtio — so the hook and its tests never read a selection proxy.
 */

import type { ArTableRowScope } from '#types/System.Artifacts.types';
import type { ArTableSelectionScope } from '../selection';

/** The two `ArTableRowScope` variants the optimizer honours; a bundle is not a row set here. */
type ArTableOptimizerRowScope = Exclude<ArTableRowScope, { bundleId: string }>;

function resolveOptimizerRowScope(params: {
  selectedCount: number;
  selection: ArTableSelectionScope;
}): ArTableOptimizerRowScope {
  if (params.selectedCount === 0 || params.selection.isAllRows) return { allRows: true };
  return { rowIds: params.selection.selectedVisibleRowIds };
}

export { resolveOptimizerRowScope };
export type { ArTableOptimizerRowScope };
